import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchThankyou } from "./api";
import { themeVars } from "../theme";
import PageLoader from "../../PageLoader";
import { thankyouTemplateFor } from "./thankyou-index";

/**
 * Post-purchase thank-you page — route `/cs/:code/thankyou?order=<token>`.
 * Fetches the order + credentials, applies the course theme as CSS variables,
 * and renders the matching ThankYouTemplate{N} (1-17) — the same 17 design
 * families as the sales and checkout pages. No auth; the order token is the guard.
 */
export default function ThankYouPage() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const order = params.get("order") || "";
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let alive = true;
    fetchThankyou(code, order)
      .then((data) => alive && setState({ status: "ready", data }))
      .catch((err) =>
        alive && setState({ status: err?.response?.status === 404 ? "notfound" : "error", data: null })
      );
    return () => { alive = false; };
  }, [code, order]);

  if (state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <PageLoader embedded page="Order" primary="#10B981" />
      </div>
    );
  }

  if (state.status === "notfound" || state.status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Order not found</h1>
        <p className="mt-2 text-sm text-gray-500">This confirmation link is invalid or has expired.</p>
      </div>
    );
  }

  // Hero sub-headline + footer are thank-you appropriate (copy() reads these).
  const data = {
    ...state.data,
    sub_headline: (state.data.sub_headline || "").trim() || "You're all set — your login details are below.",
    guarantee_status: false,
  };
  const Template = thankyouTemplateFor(data.template_id);
  const vars = themeVars({ primary: data.primary, btnText: data.btnText, footerBg: data.footerBg });

  return (
    <div style={vars}>
      <Template data={data} />
    </div>
  );
}
